import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  Switch,
  InputLabel,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';

import { ApproveMember, useDivisionOptions, useTeamOptions } from '../../../state/master';
import '../ReadOnlyTable.css';

type Props = {
  open: boolean;
  onClose: () => void;
  onAccept: (rows: ApproveMember[]) => void;
  division: number;
  team?: number;
  rows: ApproveMember[];
  canEdit?: boolean;
};

const normalizeFlag = (v: any) => (Number(v) === 1 ? 1 : 0);
const normalizeId = (v: any, fallback: number) => (Number.isFinite(Number(v)) ? Number(v) : fallback);
const normalizeName = (v: any) => String(v ?? '').trim();

export default function ApproveDialog(props: Props) {
  const { open, onClose, onAccept, division, team, rows, canEdit = true } = props;

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { divisionOptions } = useDivisionOptions();
  const { teamOptions } = useTeamOptions();

  const divisionLabel = useMemo(() => {
    const div = Number(division);
    if (Number.isNaN(div)) return '';
    const found = (divisionOptions ?? []).find((d) => Number((d as any)?.department_id) === div);
    return found?.label ?? String(division);
  }, [division, divisionOptions]);

  const teamLabel = useMemo(() => {
    if (team == null) return '';
    const tid = Number(team);
    if (Number.isNaN(tid)) return '';
    const found = (teamOptions ?? []).find((t) => Number((t as any)?.team_id) === tid);
    return found?.label ?? String(team);
  }, [team, teamOptions]);

  const [working, setWorking] = useState<ApproveMember[]>([]);
  const [newName, setNewName] = useState<string>('');
  const [newAp3, setNewAp3] = useState<boolean>(false);
  const [newAm, setNewAm] = useState<boolean>(false);

  useEffect(() => {
    // rows を正規化してローカル編集用にロード
    const normalized: ApproveMember[] = (rows ?? []).map((r: any) => ({
      department_id: normalizeId(r?.department_id, division),
      team_id: normalizeId(r?.team_id, team ?? 0),
      name: normalizeName(r?.name),
      approve_3: normalizeFlag(r?.approve_3 ?? r?.approve3),
      approve_am: normalizeFlag(r?.approve_am ?? r?.approveAm),
    }));
    setWorking(normalized);
  }, [rows, division, team, open]);

  const addRow = () => {
    const name = normalizeName(newName);
    if (!name) return;
    const row: ApproveMember = {
      department_id: division,
      team_id: team ?? 0,
      name,
      approve_3: newAp3 ? 1 : 0,
      approve_am: newAm ? 1 : 0,
    };
    setWorking((prev) => [...prev, row]);
    setNewName('');
    setNewAp3(false);
    setNewAm(false);
  };

  const updateNameAt = (index: number, value: string) => {
    setWorking((prev) =>
      prev.map((r, i) => (i === index ? { ...r, name: value } as ApproveMember : r)),
    );
  };

  const toggleApprove3At = (index: number, checked: boolean) => {
    setWorking((prev) =>
      prev.map((r, i) => (i === index ? { ...r, approve_3: checked ? 1 : 0 } : r)),
    );
  };

  const toggleApproveAmAt = (index: number, checked: boolean) => {
    setWorking((prev) =>
      prev.map((r, i) => (i === index ? { ...r, approve_am: checked ? 1 : 0 } : r)),
    );
  };

  const deleteRowAt = (index: number) => {
    setWorking((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAccept = () => {
    // name が空の行を除外して返す
    const filtered = (working ?? []).map((r) => ({
      ...r,
      department_id: normalizeId(r.department_id, division),
      team_id: normalizeId(r.team_id, team ?? 0),
      name: normalizeName(r.name),
      approve_3: normalizeFlag(r.approve_3),
      approve_am: normalizeFlag(r.approve_am),
    })).filter((r) => !!r.name);
    onAccept(filtered);
  };

  return (
    <Dialog open={open} maxWidth="md" fullScreen={fullScreen} fullWidth>
      <DialogTitle>
        承認者設定{divisionLabel ? ` [${divisionLabel}]` : ''}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={12}>
              <InputLabel sx={{ fontWeight: 'bold', mb: 1 }}>承認者の追加</InputLabel>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  label="氏名"
                  size="small"
                  sx={{ backgroundColor: isDark ? '#1e293b' : 'white', minWidth: 240 }}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  disabled={!canEdit}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <InputLabel sx={{ mr: 0.5 }}>職制</InputLabel>
                  <Switch checked={newAp3} onChange={(e) => setNewAp3(e.target.checked)} disabled={!canEdit} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <InputLabel sx={{ mr: 0.5 }}>AM</InputLabel>
                  <Switch checked={newAm} onChange={(e) => setNewAm(e.target.checked)} disabled={!canEdit} />
                </Box>
                <Button variant="contained" color="success" onClick={addRow} disabled={!canEdit}>
                  追加
                </Button>
              </Box>
            </Grid>
          </Grid>

          <TableContainer component={Paper} className={canEdit ? undefined : 'read-only-table'}>
            <Table stickyHeader aria-label="approve members table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '50%' }}>氏名</TableCell>
                  <TableCell sx={{ width: 120, textAlign: 'center' }}>職制</TableCell>
                  <TableCell sx={{ width: 120, textAlign: 'center' }}>AM</TableCell>
                  <TableCell sx={{ width: 140 }}>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(working ?? []).map((row, index) => (
                  <TableRow key={index}>
                    <TableCell component="td" scope="row">
                      <TextField
                        fullWidth
                        size="small"
                        sx={{ backgroundColor: isDark ? '#1e293b' : 'white' }}
                        value={row.name}
                        onChange={(e) => updateNameAt(index, e.target.value)}
                        disabled={!canEdit}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Switch
                        checked={normalizeFlag(row.approve_3) === 1}
                        onChange={(e) => toggleApprove3At(index, e.target.checked)}
                        disabled={!canEdit}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Switch
                        checked={normalizeFlag(row.approve_am) === 1}
                        onChange={(e) => toggleApproveAmAt(index, e.target.checked)}
                        disabled={!canEdit}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => deleteRowAt(index)}
                        disabled={!canEdit}
                      >
                        削除
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {(working ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: 'text.secondary' }}>
                      承認者が未登録です。「追加」から登録してください。
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleAccept} disabled={!canEdit}>更新する</Button>
        <Button color="secondary" variant="outlined" onClick={onClose}>キャンセル</Button>
      </DialogActions>
    </Dialog>
  );
}
